import type { DerivedRow, SortState } from './model'
import type { ParserQuote } from '@/lib/types'
import {
  exchangeLabel,
  formatCountdown,
  formatInterval,
  formatPrice,
  formatVolumeUsd,
  fundingPct,
  isFundingSoon,
  pctNum,
  signOf,
  tradeUrl,
} from '@/lib/format'
import { CaretIcon, FlameIcon } from '@/components/common/icons'
import { InfoHint } from '@/components/common/InfoHint'

const EXCHANGE_HINT =
  'Ставка фандинга на этой бирже за интервал. Зелёный (плюс) — лонги платят шортам; красный (минус) — шорты платят лонгам. Под ставкой — mark price.'

/** Направление сортировки по умолчанию для столбца. */
function defaultDir(key: string): 'asc' | 'desc' {
  if (key === 'coin') return 'asc'
  if (key === 'funding') return 'asc' // ближайший фандинг — первым
  return 'desc' // спред/ставки — больший сверху
}

interface SortHeaderProps {
  label: string
  sortKey: string
  sort: SortState
  onSort: (key: string, dir: 'asc' | 'desc') => void
  align?: 'left' | 'right'
  hint?: string
}

function SortHeader({ label, sortKey, sort, onSort, align = 'right', hint }: SortHeaderProps) {
  const active = sort.key === sortKey
  const next = () => onSort(sortKey, active ? (sort.dir === 'asc' ? 'desc' : 'asc') : defaultDir(sortKey))
  return (
    <th
      className={`is-sortable${active ? ' is-sorted' : ''}${align === 'left' ? ' col-lead' : ''}`}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="th-cell">
        {/* Кнопка внутри th — доступно с клавиатуры (Enter/Space нативно). */}
        <button type="button" className="th-sort" onClick={next}>
          <span className="th-inner">
            {label}
            <CaretIcon dir={active && sort.dir === 'asc' ? 'up' : 'down'} className="sort-caret" />
          </span>
        </button>
        {hint ? <InfoHint text={hint} size={13} /> : null}
      </span>
    </th>
  )
}

function FundingCell({ quote, coin }: { quote: ParserQuote | undefined; coin: string }) {
  if (!quote) {
    return (
      <td>
        <span className="fund__na">—</span>
      </td>
    )
  }
  const sign = signOf(quote.funding_rate)
  const url = tradeUrl(quote.exchange, coin)
  const inner = (
    <>
      <span className={`fund fund--${sign}`}>{fundingPct(quote.funding_rate)}</span>
      {quote.mark_price ? <span className="fund-sub">{formatPrice(quote.mark_price)}</span> : null}
    </>
  )
  if (url) {
    return (
      <td>
        <a
          className="fund-link"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          title={`Открыть ${coin} на ${exchangeLabel(quote.exchange)}`}
        >
          {inner}
        </a>
      </td>
    )
  }
  return <td>{inner}</td>
}

interface ParserTableProps {
  rows: DerivedRow[]
  columns: string[]
  sort: SortState
  onSort: (key: string, dir: 'asc' | 'desc') => void
  nowMs: number
}

export function ParserTable({ rows, columns, sort, onSort, nowMs }: ParserTableProps) {
  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th className="col-lead cell-idx">#</th>
            <SortHeader label="Монета" sortKey="coin" sort={sort} onSort={onSort} align="left" />
            {columns.map((ex) => (
              <SortHeader
                key={ex}
                label={exchangeLabel(ex)}
                sortKey={`ex:${ex}`}
                sort={sort}
                onSort={onSort}
                hint={EXCHANGE_HINT}
              />
            ))}
            <SortHeader
              label="Фандинг итог"
              sortKey="spread"
              sort={sort}
              onSort={onSort}
              hint="Размах ставок фандинга между выбранными биржами (макс − мин). Информационная метрика обзора, не итоговое решение: реальную прибыль с учётом тейкерских издержек считает движок на этапе сделки."
            />
            <SortHeader
              label="Объём 24ч"
              sortKey="volume"
              sort={sort}
              onSort={onSort}
              hint="Максимальный суточный оборот (USDT) среди выбранных бирж монеты. Прокси ликвидности; «—» — биржа не отдаёт объём."
            />
            <th>
              <span className="th-cell">
                Интервал
                <InfoHint
                  text="Интервал начисления фандинга у ближайшей по времени ноги (напр. каждые 1ч / 4ч / 8ч)."
                  size={13}
                />
              </span>
            </th>
            <SortHeader
              label="До фандинга"
              sortKey="funding"
              sort={sort}
              onSort={onSort}
              hint="Время до ближайшего начисления фандинга среди ног пары. Оранжевым — когда осталось менее 10 минут."
            />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const soon = isFundingSoon(row.soonestFundingMs, nowMs)
            return (
              <tr key={`${row.coin}/${row.symbol.quote}`} className={row.isHot ? 'is-hot' : undefined}>
                <td className="col-lead cell-idx tnum">{i + 1}</td>
                <td className="col-lead">
                  <div className="coin">
                    <span className="coin__ico">{row.coin.slice(0, 2).toUpperCase()}</span>
                    <div>
                      <div className="coin__name">{row.coin}</div>
                      <div className="coin__sym">
                        {row.symbol.base}/{row.symbol.quote}
                      </div>
                    </div>
                  </div>
                </td>
                {columns.map((ex) => (
                  <FundingCell key={ex} quote={row.quoteByExchange.get(ex)} coin={row.coin} />
                ))}
                <td>
                  <span className="spread">
                    {row.isHot ? <FlameIcon size={13} className="spread__star" /> : null}
                    {pctNum(row.spread)}
                  </span>
                </td>
                <td className="tnum">{formatVolumeUsd(row.maxVolumeUsd)}</td>
                <td className="countdown">{formatInterval(row.soonestInterval)}</td>
                <td>
                  <span className={`countdown${soon ? ' countdown--soon' : ''}`}>
                    {formatCountdown(row.soonestFundingMs, nowMs)}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
